import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ChevronDown,
  PiggyBank,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { OrderFormData } from "../OrderForm.schema";
import { QuerySecurity } from "@/api/queries/types";

const MAX_FUNDS = 5;

type FundACProps = {
  value: string;
  isSelected: boolean;
  securities: QuerySecurity[];
  onSelect: (name: string) => void;
  onClear: () => void;
};

function FundAutocomplete({
  value,
  isSelected,
  securities,
  onSelect,
  onClear,
}: FundACProps) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  // Position dropdown synchronously before paint
  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 2,
      left: rect.left,
      width: Math.max(rect.width, 288),
      zIndex: 9999,
    });
  }, [open, query]);

  // Click-outside to close — more reliable than onBlur + setTimeout
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        !inputRef.current?.contains(e.target as Node) &&
        !listRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (isSelected) {
    return (
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <span className="truncate text-sm text-gray-800 dark:text-gray-200">
          {value}
        </span>
        <button
          type="button"
          onClick={() => {
            setQuery("");
            onClear();
          }}
          className="shrink-0 text-gray-400 hover:text-red-500 leading-none px-0.5"
          aria-label="Remove fund"
        >
          ✕
        </button>
      </div>
    );
  }

  const filtered =
    query.length >= 1
      ? securities
          .filter(
            (s) =>
              s.name?.toLowerCase().includes(query.toLowerCase()) ||
              s.security_code?.toLowerCase().includes(query.toLowerCase()) ||
              s.isin_code?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 10)
      : [];

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (e.target.value === "") onClear();
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        placeholder="Søk fond..."
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      />
      {open &&
        filtered.length > 0 &&
        createPortal(
          <ul
            ref={listRef}
            style={dropdownStyle}
            className="max-h-52 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl text-xs"
          >
            {filtered.map((s) => (
              <li
                key={s.id}
                onMouseDown={() => {
                  setQuery(s.name);
                  onSelect(s.name);
                  setOpen(false);
                }}
                className="px-3 py-1.5 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/30 flex items-baseline justify-between gap-2"
              >
                <span className="truncate font-medium">{s.name}</span>
                <span className="shrink-0 text-gray-400 font-mono">
                  {s.security_code}
                </span>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}

type Props = { securities?: QuerySecurity[]; hideToggle?: boolean };

export function SavingsAgreementSection({
  securities = [],
  hideToggle = false,
}: Props) {
  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const enabled = useWatch({ name: "dpSavingsEnabled" }) as boolean;
  const modelType = useWatch({ name: "dpModelType" }) as string | undefined;
  const hasModel = !!modelType && modelType !== "";
  const isNoModel = modelType === "NOMODEL";

  const { fields, append, remove } = useFieldArray({
    control,
    name: "dpSavingsFunds",
  });
  const watchedFunds = (useWatch({ control, name: "dpSavingsFunds" }) ??
    []) as Array<{ name: string; pct: number }>;
  const savingsAmount =
    (useWatch({ control, name: "dpSavingsAmount" }) as number) || 0;

  const distributeEvenly = () => {
    const n = fields.length;
    if (n === 0) return;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    fields.forEach((_, i) => {
      setValue(
        `dpSavingsFunds.${i}.pct`,
        i === n - 1 ? base + remainder : base,
        { shouldValidate: true }
      );
    });
  };

  const resetPcts = () => {
    fields.forEach((_, i) => {
      setValue(`dpSavingsFunds.${i}.pct`, 0, { shouldValidate: true });
    });
  };

  const toggle = () => {
    const next = !enabled;
    setValue("dpSavingsEnabled", next, { shouldValidate: true });
    // Clear fields when deactivating
    if (!next) {
      setValue("dpSavingsAmount", undefined);
      setValue("dpSavingsDayOfMonth", undefined);
      setValue("dpSavingsAvtalegiro", false);
      setValue("dpSavingsFunds", []);
    }
  };

  return (
    <div className="mt-2">
      {/* Toggle header — hidden when used inside AgreementsGroup */}
      {!hideToggle && (
        <button
          type="button"
          onClick={toggle}
          disabled={!hasModel}
          title={
            !hasModel
              ? "Velg en modellportefølje for å aktivere spareavtale"
              : undefined
          }
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors
            ${
              !hasModel
                ? "border-dashed border-gray-200 bg-transparent text-gray-300 cursor-not-allowed dark:border-gray-700 dark:text-gray-600"
                : enabled
                ? "border-emerald-400 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                : "border-dashed border-gray-300 bg-transparent text-gray-500 hover:border-gray-400 hover:text-gray-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500"
            }`}
        >
          <span className="flex items-center gap-2">
            <PiggyBank className="h-4 w-4 flex-shrink-0" />
            Spareavtale
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              enabled ? "rotate-180" : ""
            }`}
          />
        </button>
      )}

      {/* Collapsible body — only shown & required when enabled */}
      {enabled && (
        <div
          className={`border border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/60 dark:bg-emerald-950/10 space-y-3 ${
            hideToggle
              ? "rounded-lg px-4 pt-2 pb-4"
              : "mt-1 rounded-b-lg border-t-0 px-4 py-4"
          }`}
        >
          {/* Section label — only shown when toggle is external */}
          {hideToggle && (
            <div className="flex items-center gap-2 py-2 border-b border-emerald-200 dark:border-emerald-900/60 mb-1">
              <PiggyBank className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                Spareavtale
              </span>
            </div>
          )}
          {/* Sparebeløp */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Savings amount:
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex flex-col gap-1">
              <div className="relative w-full">
                <input
                  type="number"
                  min={0}
                  step={100}
                  placeholder="0"
                  {...register("dpSavingsAmount", { valueAsNumber: true })}
                  className={`w-full rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    errors.dpSavingsAmount
                      ? "border-red-400 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                  kr
                </span>
              </div>
              {errors.dpSavingsAmount && (
                <p className="text-xs text-red-500">
                  {errors.dpSavingsAmount.message}
                </p>
              )}
            </div>
          </div>

          {/* Trekkdato */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Debit date:
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={31}
                  step={1}
                  placeholder="1"
                  {...register("dpSavingsDayOfMonth", { valueAsNumber: true })}
                  className={`w-20 rounded-md border bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    errors.dpSavingsDayOfMonth
                      ? "border-red-400 dark:border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  dag i måneden
                </span>
              </div>
              {errors.dpSavingsDayOfMonth && (
                <p className="text-xs text-red-500">
                  {errors.dpSavingsDayOfMonth.message}
                </p>
              )}
            </div>
          </div>

          {/* Avtalegiro */}
          <div className="grid grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
              Avtalegiro:
            </label>
            <label className="relative inline-flex w-fit cursor-pointer items-center">
              <input
                type="checkbox"
                {...register("dpSavingsAvtalegiro")}
                className="peer sr-only"
              />
              <div className="h-6 w-11 rounded-full bg-gray-200 dark:bg-gray-700 peer-checked:bg-emerald-500 transition-colors peer-focus:ring-2 peer-focus:ring-emerald-500 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-all peer-checked:after:translate-x-5" />
            </label>
          </div>

          {/* Fund rows — only when Ingen model is selected */}
          {isNoModel && (
            <div className="pt-1 space-y-2">
              <div className="grid grid-cols-[180px_1fr] items-start gap-3">
                <span className="text-sm font-medium text-right text-gray-600 dark:text-gray-400 pt-1.5">
                  Fond:
                </span>
                <div className="space-y-2">
                  {/* Action toolbar — shown when 2+ funds exist */}
                  {fields.length >= 2 && (
                    <div className="flex items-center justify-end gap-1.5 pb-0.5">
                      <button
                        type="button"
                        onClick={distributeEvenly}
                        title="Del 100 % likt mellom alle fond"
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-gray-800 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
                      >
                        <SlidersHorizontal className="h-3 w-3" />
                        Fordel jevnt
                      </button>
                      <button
                        type="button"
                        onClick={resetPcts}
                        title="Nullstill alle prosentandeler"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Nullstill %
                      </button>
                    </div>
                  )}
                  {fields.map((field, i) => {
                    const fundName = watchedFunds[i]?.name ?? "";
                    const pct = watchedFunds[i]?.pct || 0;
                    const fundKr =
                      savingsAmount > 0
                        ? Math.round((pct / 100) * savingsAmount)
                        : null;
                    const usedNames = new Set(
                      watchedFunds
                        .filter((_, j) => j !== i)
                        .map((f) => f?.name)
                        .filter(Boolean)
                    );
                    const availableSecurities = securities.filter(
                      (s) => !usedNames.has(s.name)
                    );
                    return (
                      <div key={field.id} className="flex items-center gap-2">
                        <FundAutocomplete
                          value={fundName}
                          isSelected={!!fundName}
                          securities={availableSecurities}
                          onSelect={(name) =>
                            setValue(`dpSavingsFunds.${i}.name`, name)
                          }
                          onClear={() =>
                            setValue(`dpSavingsFunds.${i}.name`, "")
                          }
                        />
                        <div className="relative w-20 flex-shrink-0">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            placeholder="0"
                            {...register(`dpSavingsFunds.${i}.pct`, {
                              valueAsNumber: true,
                            })}
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 pr-7 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                            %
                          </span>
                        </div>
                        {fundKr !== null && (
                          <span className="w-20 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 text-right tabular-nums">
                            {fundKr.toLocaleString("nb-NO")} kr
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => remove(i)}
                          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                  {fields.length < MAX_FUNDS && (
                    <button
                      type="button"
                      onClick={() => append({ name: "", pct: 0 })}
                      className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Legg til fond
                    </button>
                  )}
                  {/* Total row */}
                  {fields.length > 0 &&
                    (() => {
                      const totalPct = watchedFunds.reduce(
                        (sum, f) => sum + (f?.pct || 0),
                        0
                      );
                      const totalKr =
                        savingsAmount > 0
                          ? Math.round((totalPct / 100) * savingsAmount)
                          : null;
                      const isOver = totalPct > 100;
                      const isExact = totalPct === 100;
                      const colorCls = isExact
                        ? "text-emerald-600 dark:text-emerald-400"
                        : isOver
                        ? "text-red-500 dark:text-red-400"
                        : "text-amber-500 dark:text-amber-400";
                      return (
                        <div className="flex items-center gap-2 border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-0.5">
                          <span className="flex-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                            Totalt
                          </span>
                          <span
                            className={`w-20 flex-shrink-0 text-xs font-semibold text-right tabular-nums ${colorCls}`}
                          >
                            {totalPct} %
                          </span>
                          {totalKr !== null && (
                            <span
                              className={`w-20 flex-shrink-0 text-xs font-semibold text-right tabular-nums ${colorCls}`}
                            >
                              {totalKr.toLocaleString("nb-NO")} kr
                            </span>
                          )}
                          {/* spacer for trash button column */}
                          <div className="w-7 flex-shrink-0" />
                        </div>
                      );
                    })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
